import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { EntityRepresentativesPerson } from "../../utils/entityRepresentatives";
import {
  createEmptyEntityRepresentativeDraft,
  EntityRepresentativeDraft,
  EntityRepresentativeDraftErrors,
  normalizeEntityRepresentativePerson,
  validateEntityRepresentativeDraft,
} from "../../utils/entityRepresentativesDraft";

type AddPersonDialogProps = {
  open: boolean;
  busy: boolean;
  errorMessage: string | null;
  onClose: () => void;
  onSubmit: (person: EntityRepresentativesPerson) => Promise<void>;
};

const ROLE_GROUP_SX = {
  borderRadius: "14px",
  background: "#f7f8fa",
  padding: "12px 16px 8px",
} as const;

export const AddPersonDialog = ({
  open,
  busy,
  errorMessage,
  onClose,
  onSubmit,
}: AddPersonDialogProps) => {
  const [draft, setDraft] = useState<EntityRepresentativeDraft>(() =>
    createEmptyEntityRepresentativeDraft(),
  );
  const [errors, setErrors] = useState<EntityRepresentativeDraftErrors>({});

  useEffect(() => {
    if (open) {
      setDraft(createEmptyEntityRepresentativeDraft());
      setErrors({});
    }
  }, [open]);

  const handleSubmit = async () => {
    const validation = validateEntityRepresentativeDraft(draft);
    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      return;
    }
    setErrors({});
    await onSubmit(normalizeEntityRepresentativePerson(draft));
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth onClose={busy ? undefined : onClose}>
      <DialogTitle>Add Person</DialogTitle>
      <DialogContent>
        {errorMessage && (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            {errorMessage}
          </Alert>
        )}
        <Stack spacing={1.5} sx={{ marginTop: 1 }}>
          <TextField
            label="First Name *"
            name="firstName"
            value={draft.firstName}
            error={Boolean(errors.firstName)}
            helperText={errors.firstName}
            disabled={busy}
            onChange={(event) => {
              setDraft((current) => ({
                ...current,
                firstName: event.target.value,
              }));
              setErrors((current) => ({ ...current, firstName: undefined }));
            }}
          />
          <TextField
            label="Last Name *"
            name="lastName"
            value={draft.lastName}
            error={Boolean(errors.lastName)}
            helperText={errors.lastName}
            disabled={busy}
            onChange={(event) => {
              setDraft((current) => ({
                ...current,
                lastName: event.target.value,
              }));
              setErrors((current) => ({ ...current, lastName: undefined }));
            }}
          />

          <Box sx={ROLE_GROUP_SX}>
            <FormControl error={Boolean(errors.roles)}>
              <Typography variant="body2">Roles *</Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    name="ubo"
                    checked={draft.ubo}
                    disabled={busy}
                    onChange={(event) => {
                      setDraft((current) => ({
                        ...current,
                        ubo: event.target.checked,
                      }));
                      setErrors((current) => ({ ...current, roles: undefined }));
                    }}
                  />
                }
                label="UBO (Individuals who hold 25% or more of the share capital or otherwise control the Entity)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="representative"
                    checked={draft.representative}
                    disabled={busy}
                    onChange={(event) => {
                      setDraft((current) => ({
                        ...current,
                        representative: event.target.checked,
                      }));
                      setErrors((current) => ({ ...current, roles: undefined }));
                    }}
                  />
                }
                label="Representative"
              />
              {errors.roles && <FormHelperText>{errors.roles}</FormHelperText>}
            </FormControl>
          </Box>

          <Typography variant="body2" color="text.secondary">
            Email or phone is required.
          </Typography>
          <TextField
            label="Email"
            name="email"
            value={draft.email}
            error={Boolean(errors.email)}
            helperText={errors.email}
            disabled={busy}
            onChange={(event) => {
              setDraft((current) => ({
                ...current,
                email: event.target.value,
              }));
              setErrors((current) => ({
                ...current,
                email: undefined,
                contact: undefined,
              }));
            }}
          />
          <TextField
            label="Phone"
            name="phone"
            value={draft.phone}
            disabled={busy}
            onChange={(event) => {
              setDraft((current) => ({
                ...current,
                phone: event.target.value,
              }));
              setErrors((current) => ({ ...current, contact: undefined }));
            }}
          />
          {errors.contact && (
            <FormHelperText error>{errors.contact}</FormHelperText>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ padding: "16px 24px" }}>
        <Button onClick={onClose} disabled={busy} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={busy}
          variant="contained"
          color="primary"
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};
